import { Box, SimpleGrid, Flex, Text, Badge, Spinner, Icon, useColorModeValue } from '@chakra-ui/react';
import { MdChevronRight } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import Card from 'components/card/Card';
import { api } from 'lib/api';
import { useApi } from 'lib/useApi';
import { useAuth } from 'lib/auth';
import { TrendChart } from 'components/vdh/Charts';

const vnd = (n) => `${Number(n || 0).toLocaleString('vi-VN')}₫`;

/** Ô số liệu lớn — có thể bấm để đi tới trang liên quan. */
function BigStat({ icon, bg, label, value, to }) {
  const navigate = useNavigate();
  const clickable = !!to;
  return (
    <Card py="16px" cursor={clickable ? 'pointer' : 'default'} transition="all .15s"
      onClick={clickable ? () => navigate(to) : undefined}
      _hover={clickable ? { transform: 'translateY(-2px)', boxShadow: '0 12px 26px rgba(85,60,160,.15)' } : {}}>
      <Flex align="center" gap="13px">
        <Flex w="46px" h="46px" borderRadius="13px" bg={bg} align="center" justify="center" fontSize="21px" flex="0 0 auto">{icon}</Flex>
        <Box flex="1" minW="0">
          <Text fontSize="22px" fontWeight="850" lineHeight="1">{value}</Text>
          <Text fontSize="12px" color="secondaryGray.600" mt="3px" noOfLines={1}>{label}</Text>
        </Box>
        {clickable && <Icon as={MdChevronRight} color="secondaryGray.500" fontSize="20px" />}
      </Flex>
    </Card>
  );
}

export default function Dashboard() {
  const { account } = useAuth();
  const isAdmin = account?.role === 'admin';
  const { data, loading, error } = useApi(() => api.get('/reports/dashboard'));

  if (loading) return <Box pt={{ base: '130px', md: '80px' }}><Spinner /></Box>;
  if (error) return <Box pt={{ base: '130px', md: '80px' }}><Text color="red.500">{error}</Text></Box>;

  const active = data.children.filter((c) => c.correct + c.wrong + c.minutes > 0);
  const todayCorrect = data.children.reduce((s, c) => s + c.correct, 0);
  const todayWrong = data.children.reduce((s, c) => s + c.wrong, 0);
  const todayMinutes = data.children.reduce((s, c) => s + c.minutes, 0);
  const todayAcc = todayCorrect + todayWrong ? Math.round((todayCorrect / (todayCorrect + todayWrong)) * 100) : 0;
  // Bé cần nhắc học: hôm nay chưa học (không nằm trong "active"), ưu tiên bé vắng lâu. Chỉ cho phụ huynh.
  const idleKids = isAdmin ? [] : data.children
    .filter((c) => c.correct + c.wrong + c.minutes === 0 && (c.idleDays === null || c.idleDays >= 1))
    .sort((a, b) => (b.idleDays ?? 999) - (a.idleDays ?? 999))
    .slice(0, 3);
  const weak = data.weakTopics || [];

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      {/* Welcome */}
      <Box mb="20px" p="24px 26px" borderRadius="22px" color="#fff" position="relative" overflow="hidden"
        bgGradient="linear(120deg, #7C5CFC, #9B5CFC 60%, #5AC8FA)" boxShadow="0 20px 50px rgba(85,60,160,.25)">
        <Flex align="center" gap="20px" wrap="wrap">
          <Flex w="66px" h="66px" borderRadius="20px" bg="whiteAlpha.300" align="center" justify="center" fontSize="36px">🗺️</Flex>
          <Box>
            <Text fontSize="22px" fontWeight="850">{isAdmin ? 'Xin chào Quản trị 👑' : 'Chào ba mẹ 👋'}</Text>
            <Text color="whiteAlpha.900" fontSize="14px" mt="3px">
              {data.totals.children} bé đang học cùng Vui Để Học · {active.length} bé học hôm nay
            </Text>
          </Box>
          <Flex ml="auto" gap="26px" wrap="wrap">
            <HeaderKpi value={data.totals.online} label="Đang online" />
            <HeaderKpi value={`${todayMinutes}′`} label="Phút học hôm nay" />
            <HeaderKpi value={`${todayAcc}%`} label="Độ chính xác" />
            <HeaderKpi value={data.pendingRedemptions} label="Chờ duyệt thưởng" />
          </Flex>
        </Flex>
      </Box>

      <SimpleGrid columns={{ base: 2, md: 3, xl: 6 }} gap="14px" mb="20px">
        <BigStat icon="👧" bg="rgba(124,92,252,.14)" value={data.totals.children} label="Con trong nhà" />
        <BigStat icon="📱" bg="rgba(56,189,248,.16)" value={data.totals.devices} label="Thiết bị" to="/admin/devices" />
        <BigStat icon="📶" bg="rgba(34,197,94,.15)" value={data.totals.online} label="Đang online" />
        <BigStat icon="✅" bg="rgba(34,197,94,.12)" value={todayCorrect} label="Câu đúng hôm nay" />
        <BigStat icon="🎯" bg="rgba(59,130,246,.14)" value={`${todayAcc}%`} label="Chính xác hôm nay" />
        <BigStat icon="🎁" bg="rgba(255,207,74,.18)" value={data.pendingRedemptions} label="Chờ duyệt" to="/admin/rewards" />
      </SimpleGrid>

      {/* Biểu đồ xu hướng — full width cho dễ nhìn */}
      <Card mb="20px">
        <Flex align="center" mb="6px">
          <Text fontWeight="850" fontSize="16px">📈 Xu hướng 7 ngày · tổng câu đúng</Text>
          <Badge ml="auto" colorScheme="green">{todayCorrect} câu hôm nay</Badge>
        </Flex>
        <TrendChart data={data.trend} />
      </Card>

      <SimpleGrid columns={{ base: 1, lg: 2 }} gap="18px" alignItems="start">
        {/* Trái: hoạt động + xu hướng */}
        <Box>
          <Card mb="18px">
            <Flex align="center" mb="10px">
              <Text fontWeight="850" fontSize="15px">🌟 Hoạt động hôm nay</Text>
              <Badge ml="10px" colorScheme={active.length ? 'green' : 'gray'}>{active.length} bé</Badge>
              {todayMinutes > 0 && <Text ml="auto" fontSize="12.5px" color="secondaryGray.600">Tổng {todayMinutes}′ · {todayCorrect + todayWrong} câu</Text>}
            </Flex>
            {active.length === 0
              ? <Text color="secondaryGray.600" fontSize="sm">Hôm nay chưa có bé nào học. Nhắc bé mở app nhé!</Text>
              : active.map((c) => {
                const acc = c.correct + c.wrong ? Math.round(c.correct / (c.correct + c.wrong) * 100) : 0;
                return (
                  <Flex key={c.childId} align="center" gap="12px" py="11px" borderBottom="1px solid" borderColor="line">
                    <Flex w="34px" h="34px" borderRadius="10px" bg="rgba(124,92,252,.14)" align="center" justify="center" fontSize="16px" flex="0 0 auto">🧒</Flex>
                    <Box flex="1" minW="0">
                      <Flex align="center" gap="8px" wrap="wrap">
                        <Text fontWeight="800" noOfLines={1}>{c.name}</Text>
                        <Badge colorScheme={acc >= 80 ? 'green' : acc >= 50 ? 'yellow' : 'red'} fontSize="10px">{acc}%</Badge>
                        {c.streak > 0 && <Text fontSize="11.5px" title="Chuỗi ngày học">🔥 {c.streak}</Text>}
                        {c.diamonds > 0 && <Text fontSize="11.5px" title="Kim cương">💎 {c.diamonds}</Text>}
                      </Flex>
                      <Box mt="6px" h="8px" maxW="220px" borderRadius="99px" bg="blackAlpha.200" overflow="hidden">
                        <Box h="100%" w={`${acc}%`} bgGradient="linear(90deg, #22C55E, #4ade80)" />
                      </Box>
                    </Box>
                    <Box textAlign="right" fontSize="13px">
                      <Text fontWeight="800">{c.minutes}′</Text>
                      <Text color="secondaryGray.600" fontSize="12px">✅ {c.correct} · ❌ {c.wrong}</Text>
                    </Box>
                  </Flex>
                );
              })}
          </Card>
          {weak.length > 0 && <WeakTopicsCard weak={weak} />}
        </Box>

        {/* Phải: cần chú ý + admin */}
        <Box>
          <Card mb="18px">
            <Text fontWeight="850" fontSize="15px" mb="12px">🔔 Cần chú ý</Text>
            {data.pendingRedemptions > 0 && (
              <Attn ic="🎁" bg="rgba(255,207,74,.2)" t={`${data.pendingRedemptions} yêu cầu đổi thưởng`} s="Chờ ba mẹ duyệt" go="Đổi thưởng" to="/admin/rewards" />
            )}
            {idleKids.map((c) => (
              <Attn key={c.childId} ic="⏰" bg="rgba(124,92,252,.16)"
                t={`${c.name} chưa học hôm nay`}
                s={c.idleDays === null ? 'Chưa học buổi nào' : `Đã ${c.idleDays} ngày chưa học`} />
            ))}
            {data.offline.map((d) => (
              <Attn key={d.id} ic="📴" bg="rgba(251,146,60,.18)" t={`${d.name} offline ${d.days} ngày`} s={`${d.platform || 'thiết bị'} · chưa học lại`} go="Xem thiết bị" to={`/admin/devices/${d.id}`} />
            ))}
            {data.pendingRedemptions === 0 && data.offline.length === 0 && idleKids.length === 0 &&
              <Text color="secondaryGray.600" fontSize="sm">Mọi thứ ổn định 🎉</Text>}
          </Card>
          {isAdmin && <AdminBlock />}
        </Box>
      </SimpleGrid>
    </Box>
  );
}

function HeaderKpi({ value, label }) {
  return (
    <Box>
      <Text fontSize="26px" fontWeight="850" lineHeight="1">{value}</Text>
      <Text fontSize="12px" color="whiteAlpha.900">{label}</Text>
    </Box>
  );
}

/** Dòng "Cần chú ý" — bấm được (nếu có `to`), điều hướng tới trang liên quan. */
function Attn({ ic, bg, t, s, go, to }) {
  const navigate = useNavigate();
  const border = useColorModeValue('#EAE7F5', '#2A2350');
  const panel = useColorModeValue('#FBFAFF', '#1E1840');
  const clickable = !!to;
  return (
    <Flex align="center" gap="12px" p="12px 14px" borderRadius="14px" mb="10px" border="1px solid"
      borderColor={border} bg={panel} cursor={clickable ? 'pointer' : 'default'} transition="all .15s"
      onClick={clickable ? () => navigate(to) : undefined}
      _hover={clickable ? { borderColor: 'brand.400', transform: 'translateY(-1px)', boxShadow: '0 8px 18px rgba(85,60,160,.12)' } : {}}>
      <Flex w="40px" h="40px" borderRadius="11px" bg={bg} align="center" justify="center" fontSize="19px" flex="0 0 auto">{ic}</Flex>
      <Box minW="0">
        <Text fontWeight="800" fontSize="14px" noOfLines={1}>{t}</Text>
        <Text fontSize="12.5px" color="secondaryGray.600" noOfLines={1}>{s}</Text>
      </Box>
      {clickable && (
        <Flex ml="auto" align="center" gap="2px" color="brand.500" flex="0 0 auto">
          <Text fontWeight="800" fontSize="12.5px" whiteSpace="nowrap">{go}</Text>
          <Icon as={MdChevronRight} fontSize="18px" />
        </Flex>
      )}
    </Flex>
  );
}

/** Chủ đề bé còn yếu — độ chính xác thấp, gợi ý luyện thêm. */
function WeakTopicsCard({ weak }) {
  return (
    <Card>
      <Text fontWeight="850" fontSize="15px" mb="10px">📚 Chủ đề cần luyện thêm</Text>
      {weak.map((w) => (
        <Flex key={w.topic} align="center" gap="12px" py="9px">
          <Box flex="1" minW="0">
            <Flex justify="space-between" mb="5px">
              <Text fontSize="13px" fontWeight="700" noOfLines={1}>{w.topic}</Text>
              <Text fontSize="12px" color="secondaryGray.600">{w.correct}/{w.total} · {w.pct}%</Text>
            </Flex>
            <Box h="8px" borderRadius="99px" bg="blackAlpha.200" overflow="hidden">
              <Box h="100%" w={`${w.pct}%`}
                bgGradient={w.pct < 50 ? 'linear(90deg,#EF4444,#F87171)' : 'linear(90deg,#F59E0B,#FBBF24)'} />
            </Box>
          </Box>
        </Flex>
      ))}
    </Card>
  );
}

function AdminBlock() {
  const navigate = useNavigate();
  const { data } = useApi(() => api.get('/admin/overview'));
  if (!data) return null;
  const conversion = data.accounts ? Math.round((data.byTier.pro / data.accounts) * 100) : 0;
  const Row = ({ k, v, to, hint }) => (
    <Flex justify="space-between" align="center" py="8px" borderBottom="1px solid" borderColor="line"
      cursor={to ? 'pointer' : 'default'} onClick={to ? () => navigate(to) : undefined}
      _hover={to ? { color: 'brand.500' } : {}}>
      <Text color="secondaryGray.600">{k}</Text>
      <Flex align="center" gap="4px">
        <Text fontWeight="800">{v}</Text>
        {hint && <Text fontSize="11px" color="secondaryGray.500">{hint}</Text>}
        {to && <Icon as={MdChevronRight} fontSize="16px" color="secondaryGray.500" />}
      </Flex>
    </Flex>
  );
  return (
    <>
      <Card mb="18px">
        <Text fontWeight="850" fontSize="15px" mb="8px">🗂️ Toàn hệ thống <Badge ml="6px" colorScheme="purple">admin</Badge></Text>
        <Row k="Doanh thu tháng (MRR)" v={vnd(data.mrr)} />
        <Row k="Doanh thu 30 ngày" v={vnd(data.revenue30)} />
        <Row k="Đăng ký mới" v={`+${data.newAccounts7}`} hint={`7 ngày · +${data.newAccounts30}/30 ngày`} to="/admin/users" />
        <Row k="Bé hoạt động" v={data.dau} hint={`hôm nay · ${data.wau}/tuần`} />
        <Row k="Tài khoản" v={data.accounts} to="/admin/users" />
        <Row k="Đang trả phí (Pro)" v={data.activeSubs} hint={`${conversion}% chuyển đổi`} to="/admin/users" />
        <Row k="Ngân hàng câu" v={data.banks} to="/admin/questions" />
        <Flex justify="space-between" py="8px"><Text color="secondaryGray.600">Tổng câu hỏi</Text><Text fontWeight="800">{Number(data.questions).toLocaleString('vi-VN')}</Text></Flex>
        <Flex gap="6px" mt="10px" wrap="wrap">
          <Badge>Free {data.byTier.free}</Badge>
          <Badge colorScheme="blue">Standard {data.byTier.standard}</Badge>
          <Badge colorScheme="green">Pro {data.byTier.pro}</Badge>
        </Flex>
      </Card>

      {data.topWrong && data.topWrong.length > 0 && (
        <Card>
          <Flex align="center" mb="10px">
            <Text fontWeight="850" fontSize="15px">❌ Câu hỏi sai nhiều nhất</Text>
            <Text ml="auto" fontSize="12px" color="brand.500" cursor="pointer" fontWeight="800"
              onClick={() => navigate('/admin/questions')}>Ngân hàng câu →</Text>
          </Flex>
          {data.topWrong.map((q, i) => {
            const pct = q.attempts ? Math.round((q.wrong / q.attempts) * 100) : 0;
            return (
              <Flex key={i} align="center" gap="10px" py="8px" borderBottom="1px solid" borderColor="line">
                <Text fontSize="13px" flex="1" noOfLines={1}>{q.q}</Text>
                <Badge colorScheme="red" flex="0 0 auto">{q.wrong} sai · {pct}%</Badge>
              </Flex>
            );
          })}
        </Card>
      )}
    </>
  );
}
